import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from './ui/Input.jsx';
import { Button } from './ui/Button.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { overlayDismiss } from '../lib/overlayDismiss';

/**
 * ChangePasswordModal — 已登入者站內直接改密碼（不寄信）。
 * 流程：輸入目前密碼 → re-auth → updatePassword（見 AuthContext.changePassword）。
 * 只對 email/密碼帳號開放（Google 登入帳號無密碼，呼叫端用 hasPassword 控制是否顯示入口）。
 */
export function ChangePasswordModal({ open, onClose }) {
    const { t } = useTranslation('common');
    const { changePassword } = useAuth();

    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    if (!open) return null;

    function reset() {
        setCurrent(''); setNext(''); setConfirm('');
        setSaving(false); setError(''); setDone(false);
    }

    function handleClose() {
        reset();
        onClose();
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        if (next.length < 6) { setError(t('pw.errTooShort')); return; }
        if (next !== confirm) { setError(t('pw.errMismatch')); return; }
        setSaving(true);
        try {
            await changePassword(current, next);
            setDone(true);
        } catch (err) {
            const code = err?.code || err?.message || String(err);
            if (code.includes('wrong-password') || code.includes('invalid-credential')) {
                setError(t('pw.errWrongCurrent'));
            } else if (code.includes('weak-password')) {
                setError(t('pw.errTooShort'));
            } else if (code.includes('too-many-requests')) {
                setError(t('pw.errTooMany'));
            } else {
                setError(t('pw.errGeneric', { msg: code }));
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4" {...overlayDismiss(handleClose)}>
            <div
                className="w-full max-w-sm bg-cream-50 rounded-xl shadow-xl border border-cream-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2 text-ink-900 font-semibold">
                        <KeyRound size={16} /> {t('pw.title')}
                    </div>
                    <button onClick={handleClose} className="text-ink-400 hover:text-ink-700 transition">
                        <X size={18} />
                    </button>
                </div>

                {done ? (
                    <div className="px-5 py-8 text-center">
                        <CheckCircle2 size={40} className="mx-auto text-accent-500 mb-3" />
                        <p className="text-sm text-ink-700">{t('pw.success')}</p>
                        <Button variant="primary" className="mt-5 w-full" onClick={handleClose}>
                            {t('pw.close')}
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
                        <Input
                            type="password"
                            autoComplete="current-password"
                            label={t('pw.current')}
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            required
                        />
                        <Input
                            type="password"
                            autoComplete="new-password"
                            label={t('pw.new')}
                            hint={t('pw.newHint')}
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            required
                        />
                        <Input
                            type="password"
                            autoComplete="new-password"
                            label={t('pw.confirm')}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                        />
                        {error && <p className="text-xs text-danger-500">{error}</p>}
                        <div className="flex gap-2 pt-1">
                            <Button type="button" variant="outline" className="flex-1" onClick={handleClose} disabled={saving}>
                                {t('pw.cancel')}
                            </Button>
                            <Button type="submit" variant="primary" className="flex-1" disabled={saving}>
                                {saving ? <Loader2 size={14} className="animate-spin" /> : t('pw.submit')}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
